import { useState, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';

interface ModuloContattiSectionProps {
  sectionRef?: React.RefObject<HTMLElement | null>;
}

export default function ModuloContattiSection({ sectionRef }: ModuloContattiSectionProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);

    const interests: string[] = [];
    formData.forEach((value, key) => {
      if (key === 'interesse') {
        interests.push(value as string);
      } else {
        data.append(key, value as string);
      }
    });
    if (interests.length > 0) {
      data.append('interesse', interests.join(', '));
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d6uk6vudp0k8rarn7lt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Si è verificato un errore. Riprova o contattaci direttamente.');
      }
    } catch {
      setError('Errore di rete. Controlla la connessione e riprova.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full bg-black/50 border border-yellow-600/25 text-white placeholder-gray-600 rounded-md px-4 py-4 text-sm focus:outline-none focus:border-yellow-500 focus:shadow-lg transition-all duration-300';

  return (
    <section
      className="py-16 md:py-28 relative overflow-hidden"
      id="contatti"
      ref={sectionRef as React.RefObject<HTMLElement>}
      style={{
        background: 'linear-gradient(180deg, #0a0a0a 0%, #0f0d08 100%)',
      }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/4f8761f12a8ce8a08958e834b54e2e19.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-8"
          style={{ opacity: 0.07 }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]" />
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span
            className="inline-block border border-yellow-600/60 text-yellow-500 text-xs tracking-widest uppercase px-5 py-2 rounded-full mb-6"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Iscrizione Gratuita
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Prenota il Tuo{' '}
            <span style={{ color: '#D4AF37' }}>Posto</span>
          </h2>
          <p
            className="text-gray-400 text-sm leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
          >
            Compila il modulo e ti ricontatteremo per confermare la tua partecipazione.
          </p>
        </div>

        {/* Form Card */}
        <div
          className="rounded-2xl border-2 p-5 md:p-10 lg:p-12"
          style={{
            borderColor: '#D4AF37',
            background: 'linear-gradient(135deg, #141414 0%, #0d0d0d 100%)',
            boxShadow: '0 20px 60px rgba(212, 175, 55, 0.25)',
          }}
        >
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-yellow-500 mx-auto mb-6">
                <i className="ri-check-line text-4xl" style={{ color: '#D4AF37' }} />
              </div>
              <h3
                className="text-3xl font-bold text-white mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Iscrizione Ricevuta!
              </h3>
              <p
                className="text-gray-400 text-sm leading-relaxed"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
              >
                Grazie per il tuo interesse! Ti contatteremo presto per confermare
                il tuo posto al Casting Open Day del 30 Marzo.
              </p>
              <div
                className="mt-6 text-yellow-500 text-xs tracking-widest uppercase"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Filmodeling · Casting Open Day · 30 Marzo 2026
              </div>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              {/* Nome Cognome */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    className="block text-xs tracking-widest uppercase text-gray-400 mb-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Nome *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    required
                    placeholder="Il tuo nome"
                    className={inputClass}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label
                    className="block text-xs tracking-widest uppercase text-gray-400 mb-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Cognome *
                  </label>
                  <input
                    type="text"
                    name="cognome"
                    required
                    placeholder="Il tuo cognome"
                    className={inputClass}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* Email Telefono */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    className="block text-xs tracking-widest uppercase text-gray-400 mb-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="tua@email.com"
                    className={inputClass}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label
                    className="block text-xs tracking-widest uppercase text-gray-400 mb-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Telefono / WhatsApp *
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    required
                    placeholder="+39 000 000 0000"
                    className={inputClass}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* Età e Interesse */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    className="block text-xs tracking-widest uppercase text-gray-400 mb-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Età *
                  </label>
                  <input
                    type="number"
                    name="eta"
                    required
                    min={14}
                    max={65}
                    placeholder="La tua età"
                    className={inputClass}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label
                    className="block text-xs tracking-widest uppercase text-gray-400 mb-2"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Interessato/a a *
                  </label>
                  <select
                    name="interesse"
                    required
                    className={`${inputClass} cursor-pointer`}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px' }}
                  >
                    <option value="" disabled selected className="bg-[#141414]">
                      Seleziona un settore
                    </option>
                    <option value="Moda / Modelling" className="bg-[#141414]">
                      Moda / Modelling
                    </option>
                    <option value="Cinema / Recitazione" className="bg-[#141414]">
                      Cinema / Recitazione
                    </option>
                    <option value="Entrambi" className="bg-[#141414]">
                      Entrambi
                    </option>
                  </select>
                </div>
              </div>

              {/* Messaggio opzionale */}
              <div>
                <label
                  className="block text-xs tracking-widest uppercase text-gray-400 mb-2"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Messaggio (facoltativo) {charCount}/500
                </label>
                <textarea
                  name="messaggio"
                  maxLength={500}
                  rows={3}
                  placeholder="Presentati brevemente o comunicaci qualcosa che ritieni utile..."
                  className={inputClass}
                  style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', resize: 'none' }}
                  onChange={(e) => {
                    if (e.target.value.length > 500) {
                      e.target.value = e.target.value.slice(0, 500);
                    }
                    setCharCount(e.target.value.length);
                  }}
                />
              </div>

              {/* Privacy */}
              <div className="flex items-start gap-3 pt-2">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    name="privacy"
                    id="privacy"
                    required
                    className="cursor-pointer accent-yellow-500"
                    style={{ width: '22px', height: '22px', minWidth: '22px' }}
                  />
                </div>
                <label
                  htmlFor="privacy"
                  className="text-xs text-gray-400 leading-relaxed cursor-pointer"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Acconsento al trattamento dei miei dati personali ai sensi del GDPR (Regolamento UE 2016/679) per la gestione della mia richiesta di partecipazione all&apos;Open Day. I dati non saranno ceduti a terzi.{' '}
                  <Link to="/privacy-policy" className="underline cursor-pointer" style={{ color: '#D4AF37' }}>
                    Leggi la Privacy Policy
                  </Link>{' '}
                  *
                </label>
              </div>

              {/* Error */}
              {error && (
                <p className="text-red-400 text-xs text-center" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {error}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 md:py-5 rounded-md font-bold tracking-widest uppercase transition-all duration-300 cursor-pointer hover:shadow-2xl hover:shadow-yellow-700/40 hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-xs md:text-sm"
                style={{
                  background: loading ? '#888' : 'linear-gradient(90deg, #b8860b, #D4AF37, #f0d060, #D4AF37, #b8860b)',
                  color: '#000',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <i className="ri-star-fill text-sm flex-shrink-0" />
                    <span className="md:hidden">Voglio Partecipare</span>
                    <span className="hidden md:inline">Voglio Partecipare al Casting</span>
                    <i className="ri-star-fill text-sm flex-shrink-0" />
                  </>
                )}
              </button>

              <p
                className="text-center text-gray-600 text-xs"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Partecipazione gratuita · Posti limitati · Risposta entro 24h
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
